import {useCallback, useMemo, useReducer} from 'react'
import some from 'lodash/some'
import {decode} from 'he'
import algoliasearch, {SearchClient} from 'algoliasearch/lite'
import youtubeSearch from 'youtube-search'

export enum SearchTarget {
  Algolia,
  YouTube,
}

export enum SearchSort {
  Relevance,
  Date,
}

export interface SearchResult {
  id: string
  title: string
  summary: string
  link: string
  heroUrl?: string
  publishedAt: string
  updatedAt?: string
}

export interface SearchParameters {
  query: string
  target: SearchTarget
  sort: SearchSort
}

interface SearchState {
  searchResults: SearchResult[] | null
  existingSearch: SearchParameters | null
  nextPageToken: string | null
  status: 'idle' | 'pending' | 'resolved' | 'rejected'
}

type UseSearch = (props: {
  resultsPerPage: number
  algoliaTagFilters?: string[] | string[][]
}) => {
  status: SearchState['status']
  searchResults: SearchResult[] | null // non-null when status === 'resolved'
  existingSearch: SearchParameters | null // non-null when status === 'resolved
  search: (params: SearchParameters) => void
  loadMore: () => void
  hasMore: boolean
}

const useSearch: UseSearch = ({resultsPerPage, algoliaTagFilters}) => {
  const [{searchResults, nextPageToken, existingSearch, status}, dispatch] =
    useReducer(searchStateReducer, emptySearchState)

  const algoliaClient = useAlgoliaSearchClient()

  const search = useCallback(
    (searchParameters: SearchParameters, rawPageToken?: string) => {
      const pageToken = rawPageToken ?? null

      dispatch({type: 'pending', pageToken})

      executeSearch({
        searchParameters,
        pageToken,
        resultsPerPage,
        algoliaClient,
        algoliaTagFilters,
      })
        .then((payload) => {
          dispatch({
            type: 'resolved',
            pageToken,
            searchParameters,
            ...payload,
          })
        })
        .catch(() => dispatch({type: 'rejected'}))
    },
    [resultsPerPage, algoliaClient, algoliaTagFilters]
  )

  const loadMore = useCallback(() => {
    if (existingSearch == null || nextPageToken == null) {
      return
    }

    search(existingSearch, nextPageToken)
  }, [search, existingSearch, nextPageToken])

  return {
    status,
    searchResults,
    existingSearch,
    search,
    loadMore,
    hasMore: nextPageToken != null,
  }
}

const emptySearchState: SearchState = {
  searchResults: null,
  existingSearch: null,
  nextPageToken: null,
  status: 'idle',
}

type SearchStateAction =
  | {type: 'pending'; pageToken: string | null}
  | {type: 'rejected'}
  | {
      type: 'resolved'
      pageToken: string | null
      searchParameters: SearchParameters
      newResults: SearchResult[]
      nextPageToken: string | null
    }

const searchStateReducer = (
  state: SearchState,
  action: SearchStateAction
): SearchState => {
  switch (action.type) {
    case 'pending':
      return {
        ...(action.pageToken === null ? emptySearchState : state),
        status: 'pending',
      }

    case 'resolved': {
      const {searchResults} = state
      const {pageToken, newResults, searchParameters, nextPageToken} = action

      const existingResults =
        pageToken !== null && searchResults !== null ? searchResults : []

      const newResultsUnique = newResults.filter(
        ({id}) => some(existingResults, {id}) === false
      )

      return {
        searchResults: [...existingResults, ...newResultsUnique],
        existingSearch: searchParameters,
        nextPageToken,
        status: 'resolved',
      }
    }

    case 'rejected':
      return {...state, status: 'rejected'}

    default:
      throw new Error('Unhandled action type')
  }
}

const useAlgoliaSearchClient = () =>
  useMemo(
    () =>
      algoliasearch(
        process.env.GATSBY_ALGOLIA_APP_ID,
        process.env.GATSBY_ALGOLIA_SEARCH_KEY
      ),
    []
  )

interface ExecuteParams {
  searchParameters: SearchParameters
  resultsPerPage: number
  pageToken: string | null
}

interface AlgoliaExecuteParams {
  algoliaClient: SearchClient
  algoliaTagFilters?: string[] | string[][]
}

interface ExecutePayload {
  newResults: SearchResult[]
  nextPageToken: string | null
}

type ExecuteReturnType = Promise<ExecutePayload>

const executeSearch = (
  params: ExecuteParams & AlgoliaExecuteParams
): ExecuteReturnType => {
  const {target} = params.searchParameters

  switch (target) {
    case SearchTarget.Algolia:
      return executeAlgoliaSearch(params)

    case SearchTarget.YouTube:
      return executeYouTubeSearch(params)

    default:
      throw new Error('Unknown search target')
  }
}

const youTubeFields =
  'pageInfo,nextPageToken,items/kind,items/id,items/snippet/publishedAt,items/snippet/title,items/snippet/description,items/snippet/thumbnails/medium'

const executeYouTubeSearch = ({
  searchParameters: {query, sort},
  resultsPerPage,
  pageToken,
}: ExecuteParams): ExecuteReturnType => {
  const cacheKey = `${query}/${sort}/${resultsPerPage}/${pageToken}`

  if (getYouTubeCacheEntry(cacheKey)) {
    return Promise.resolve(getYouTubeCacheEntry(cacheKey))
  }

  return youtubeSearch(query, {
    order: sort === SearchSort.Date ? 'date' : 'relevance',
    maxResults: resultsPerPage,
    pageToken: pageToken ?? undefined,
    channelId: process.env.GATSBY_YOUTUBE_CHANNEL_ID,
    fields: youTubeFields,
    key: process.env.GATSBY_YOUTUBE_API_KEY,
  }).then((response) => {
    const newResults = response.results.map(
      ({id, link, title, description, publishedAt, thumbnails: {medium}}) => ({
        id,
        title: decode(title), // https://stackoverflow.com/questions/60668719/youtube-videos-api-snippet-title-contains-special-characters-in-next-js
        summary: description,
        link,
        publishedAt,
        heroUrl: medium?.url,
      })
    )

    const nextPageToken: string | null = response.pageInfo.nextPageToken ?? null

    const payload = {newResults, nextPageToken}

    setYouTubeCacheEntry(cacheKey, payload)

    return payload
  })
}

const youTubeSearchCache: {[key: string]: ExecutePayload} = {}

const getYouTubeCacheEntry = (key: string) => youTubeSearchCache[key]

const setYouTubeCacheEntry = (key: string, payload: ExecutePayload) =>
  (youTubeSearchCache[key] = payload)

interface AlgoliaSearchResult {
  objectID: string
  title: string
  summary: string
  permalink: string
  hero_url: string
  publication_date: string // YYYY-MM-DD
  updated_at_i: string // string containing a number representing seconds since epoch
}

const algoliaIndexBase = `${
  process.env.GATSBY_ALGOLIA_INDEX_PREFIX ?? 'dev'
}_Tile`

const algoliaIndexLookup: Record<SearchSort, string> = {
  [SearchSort.Date]: algoliaIndexBase + '_updated_at_desc',
  [SearchSort.Relevance]: algoliaIndexBase,
}

const executeAlgoliaSearch = ({
  searchParameters: {query, sort},
  resultsPerPage,
  pageToken,
  algoliaClient,
  algoliaTagFilters,
}: ExecuteParams & AlgoliaExecuteParams): ExecuteReturnType =>
  algoliaClient
    .initIndex(algoliaIndexLookup[sort])
    .search<AlgoliaSearchResult>(query, {
      hitsPerPage: resultsPerPage,
      page: pageToken != null ? Number(pageToken) : 0,
      tagFilters: algoliaTagFilters,
    })
    .then((response) => {
      const newResults = response.hits.map(
        ({
          objectID,
          title,
          summary,
          permalink,
          publication_date,
          updated_at_i,
        }) => ({
          id: objectID,
          title,
          summary,
          link: permalink,
          publishedAt: publication_date,
          updatedAt: new Date(updated_at_i).toISOString(),
        })
      )

      const hasNextPage = response.page + 1 < response.nbPages
      const nextPageToken = hasNextPage ? String(response.page + 1) : null

      return {newResults, nextPageToken}
    })

export default useSearch
